import React, { useState, useEffect } from 'react';
import './index.css';
import productsData from './data/products.json';

// ─── UTILITY ────────────────────────────────────────────────
const fmt = (n) => `$${n.toLocaleString()} USD`;

// ─── HEADER ─────────────────────────────────────────────────
// Mirrors: sections/header.liquid
// logo_position: middle-left, padding_top/bottom: 20, logo_width: 100
function Header({ onHome }) {
  return (
    <div className="section-header">
      <header className="header header--middle-left page-width color-scheme-1 gradient">
        <h1 className="header__heading">
          <span
            className="header__heading-link"
            onClick={onHome}
            role="link"
            tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && onHome()}
            style={{ cursor: 'pointer' }}
          >
            <img
              className="header__heading-logo"
              src="/assets/glitchcloud.png"
              alt="glitchclouds"
              width="100"
              onError={e => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'inline';
              }}
            />
            <span className="header__heading-logo-text" >
              glitchclouds
            </span>
          </span>
        </h1>
        <div className="header__icons">
          <span className="header__archive-note">pls email if you'd like a sweater :)</span>
        </div>
      </header>
    </div>
  );
}

// ─── FOOTER ─────────────────────────────────────────────────
function Footer() {
  return (
    <footer className="footer color-scheme-4 gradient">
      <div className="footer__content-bottom page-width">
        <p className="footer__copyright">another project by <a href="https://rachelbinx.com">rachel binx</a></p>
      </div>
    </footer>
  );
}

// ─── PRODUCT CARD ────────────────────────────────────────────
// Mirrors: snippets/card-product.liquid
// card_style: standard, card_text_alignment: center,
// card_border_thickness: 0, card_image_padding: 0, card_corner_radius: 0
function ProductCard({ product, onClick }) {
  const [imgFailed, setImgFailed] = useState(false);

  return (
    <li className="grid__item">
      <div
        className="card-wrapper"
        onClick={() => onClick(product)}
        role="link"
        tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && onClick(product)}
        style={{ cursor: 'pointer' }}
      >
        <div className="card card--standard">
          <div className="card__inner">
            {/* Cloud SVG always visible as background */}
            <div className="card__cloud-bg" aria-hidden="true">
              <img src={`/assets/${product.cloudSvg}`} alt="" />
            </div>
            <div className="card__media">
              {!imgFailed && product.images?.[0] ? (
                <img
                  src={`/assets/${product.images[0]}`}
                  alt={product.title}
                  loading="lazy"
                  onError={() => setImgFailed(true)}
                />
              ) : null}
            </div>
          </div>
          <div className="card-information">
            <h3 className="card__heading">{product.title}</h3>
            {product.price && (
              <div className="price">
                <span>${product.price.toLocaleString()}.00 USD</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}

// ─── SCROLL REVEAL HOOK ──────────────────────────────────────
// Mirrors: assets/animations.js (animations_reveal_on_scroll: true)
function useScrollReveal() {
  useEffect(() => {
    if (!window.matchMedia('(prefers-reduced-motion: no-preference)').matches) return;
    const els = document.querySelectorAll('.scroll-trigger');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });
    els.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  });
}

// ─── BANNER IMAGE ────────────────────────────────────────────
// Mirrors: sections/image-banner.liquid
// image_height: small → min-height: 42rem
// show_text_box: true → renders .banner__box if children provided
function Banner({ src, alt = '', children }) {
  return (
    <section className="section banner banner--small banner--content-align-center banner--mobile-bottom scroll-trigger animate--fade-in">
      <div className="banner__media">
        <img
          src={`/assets/${src}`}
          alt={alt}
          loading="lazy"
          onError={e => {
            e.target.parentNode.classList.add('banner__media--placeholder');
            e.target.remove();
          }}
        />
      </div>
      {children && (
        <div className="banner__content">
          <div className="banner__box content-container">
            {children}
          </div>
        </div>
      )}
    </section>
  );
}

// ─── HOME PAGE ───────────────────────────────────────────────
// Mirrors: templates/index.json — exact section order:
//   1. image-banner  cloud_sweater_plane_bg_clouds.jpg (no content)
//   2. rich-text     "behold, the glory of the stratosphere"
//   3. multicolumn   3 columns (bombay_cloud, screenshot, d692f6ea)
//   4. image-banner  DSC01816.jpg (no content)
//   5. featured-collection  frontpage — 6 products, 3 cols, no title, no view-all
//   6. image-banner  IMG_3072.jpg (no content)
//   7. featured-collection  CMYK collection — 4 products, 3 cols, view-all
//   8. image-banner  DSC01818.jpg — with text box (production note)
//   9. featured-collection  pen plotter prints — 6 products, 3 cols, view-all
function HomePage({ onProductClick }) {
  const { products } = productsData;
  useScrollReveal();

  const frontpage = products.filter(p => p.collection === 'sweaters').slice(0, 6);
  const cmyk      = products.filter(p => p.collection === 'cmyk').slice(0, 4);
  const prints    = products.filter(p => p.collection === 'prints').slice(0, 6);

  return (
    <div>

      {/* ── 1. image-banner: cloud_sweater_plane_bg_clouds.jpg ── */}
      <Banner src="cloud_sweater_plane_bg_clouds.jpg" />

      {/* ── 2. rich-text ── */}
      {/* full_width: true, content_alignment: center, padding_top: 40, padding_bottom: 52 */}
      <section className="section rich-text isolate">
        <div className="rich-text content-container rich-text--full-width content-container--full-width rich-text-padding">
          <div className="rich-text__wrapper rich-text__wrapper--center page-width">
            <div className="rich-text__blocks center scroll-trigger animate--slide-in">
              <h2 className="rich-text__heading rte inline-richtext h2">
                behold, the glory of the stratosphere
              </h2>
              <div className="rich-text__text rte">
                <p>custom-knit sweaters &amp; prints, generated by glitchy dithered clouds</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 3. multicolumn ── */}
      {/* 3 cols, no title, column_alignment: left, background_style: primary */}
      {/* padding_top: 36, padding_bottom: 36 */}
      <section className="section multicolumn background-primary color-scheme-1 gradient multicolumn-padding isolate">
        <div className="page-width">
          <ul className="multicolumn-list grid grid--3-col-desktop grid--1-col-tablet-down" role="list">

            <li className="multicolumn-list__item grid__item scroll-trigger animate--slide-in">
              <div className="multicolumn-card content-container">
                <div className="multicolumn-card__image-wrapper multicolumn-card__image-wrapper--full-width">
                  <div className="media media--adapt">
                    <img
                      className="multicolumn-card__image"
                      src="/assets/bombay_cloud.jpg"
                      alt=""
                      loading="lazy"
                      onError={e => { e.target.parentNode.style.minHeight = '20rem'; e.target.remove(); }}
                    />
                  </div>
                </div>
                <div className="multicolumn-card__info">
                  <p>for the past 4 years, I've been taking photos of clouds.</p>
                </div>
              </div>
            </li>

            <li className="multicolumn-list__item grid__item scroll-trigger animate--slide-in">
              <div className="multicolumn-card content-container">
                <div className="multicolumn-card__image-wrapper multicolumn-card__image-wrapper--full-width">
                  <div className="media media--adapt">
                    <img
                      className="multicolumn-card__image"
                      src="/assets/Screenshot_2024-10-19_at_10.27.13_AM.png"
                      alt=""
                      loading="lazy"
                      onError={e => { e.target.parentNode.style.minHeight = '20rem'; e.target.remove(); }}
                    />
                  </div>
                </div>
                <div className="multicolumn-card__info">
                  <p>I've made a modified dithering algorithm, which creates a unique glitchy pattern for each cloud.</p>
                </div>
              </div>
            </li>

            <li className="multicolumn-list__item grid__item scroll-trigger animate--slide-in">
              <div className="multicolumn-card content-container">
                <div className="multicolumn-card__image-wrapper multicolumn-card__image-wrapper--full-width">
                  <div className="media media--adapt">
                    <img
                      className="multicolumn-card__image"
                      src="/assets/d692f6ea3754408ed064715ba1317b40.png"
                      alt=""
                      loading="lazy"
                      onError={e => { e.target.parentNode.style.minHeight = '20rem'; e.target.remove(); }}
                    />
                  </div>
                </div>
                <div className="multicolumn-card__info">
                  <p>with the help of a programmable knitting machine, that pattern turns into a sweater!</p>
                </div>
              </div>
            </li>

          </ul>
        </div>
      </section>

      {/* ── 4. image-banner: DSC01816.jpg ── */}
      <Banner src="DSC01816.jpg" />

      {/* ── 5. featured-collection: frontpage ── */}
      {/* no title, 6 products, 3 cols, full_width: true, no view-all, color_scheme: scheme-1 */}
      {/* padding_top: 32, padding_bottom: 36 */}
      <section className="section color-scheme-1 gradient featured-collection-padding-frontpage">
        <div className="page-width">
          <ul className="grid grid--3-col-desktop grid--2-col-tablet-down" role="list">
            {frontpage.map(p => (
              <ProductCard key={p.id} product={p} onClick={onProductClick} />
            ))}
          </ul>
        </div>
      </section>

      {/* ── 6. image-banner: IMG_3072.jpg ── */}
      <Banner src="IMG_3072.jpg" />

      {/* ── 7. featured-collection: CMYK collection ── */}
      {/* title: "CMYK collection", 4 products, 3 cols, show_view_all: true */}
      {/* padding_top: 36, padding_bottom: 36 */}
      <section className="section featured-collection-padding">
        <div className="page-width">
          <div className="title-wrapper-with-link scroll-trigger animate--slide-in">
            <h2 className="title h2">CMYK collection</h2>
          </div>
          <ul className="grid grid--3-col-desktop grid--2-col-tablet-down" role="list">
            {cmyk.map(p => (
              <ProductCard key={p.id} product={p} onClick={onProductClick} />
            ))}
          </ul>
        </div>
      </section>

      {/* ── 8. image-banner: DSC01818.jpg — with text block ── */}
      {/* show_text_box: true, text: "Each sweater is custom-made…" */}
      <Banner src="DSC01818.jpg">
        Each sweater is custom-made on a knitting machine to reduce textile waste, with a production time of 3-4 weeks.
      </Banner>

      {/* ── 9. featured-collection: pen plotter prints ── */}
      {/* title: "pen plotter prints", 6 products, 3 cols, show_view_all: true */}
      {/* padding_top: 36, padding_bottom: 36 */}
      <section className="section featured-collection-padding">
        <div className="page-width">
          <div className="title-wrapper-with-link scroll-trigger animate--slide-in">
            <h2 className="title h2">pen plotter prints</h2>
          </div>
          <ul className="grid grid--3-col-desktop grid--2-col-tablet-down" role="list">
            {prints.map(p => (
              <ProductCard key={p.id} product={p} onClick={onProductClick} />
            ))}
          </ul>
        </div>
      </section>

    </div>
  );
}

// ─── PRODUCT PAGE ────────────────────────────────────────────
function ProductPage({ product, onBack, onProductClick }) {
  const [selectedSize, setSelectedSize] = useState(product.sizes?.[1] || product.sizes?.[0]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [failedImgs, setFailedImgs] = useState({});

  const { collections, products } = productsData;
  const col = collections[product.collection];
  const images = product.images || [];

  const related = products
    .filter(p => p.collection === product.collection && p.id !== product.id)
    .slice(0, 3);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setSelectedSize(product.sizes?.[1] || product.sizes?.[0]);
    setActiveIdx(0);
    setFailedImgs({});
  }, [product.id]);

  const activeImgFailed = failedImgs[activeIdx];
  const hasImages = images.length > 0;

  return (
    <div className="product-page-wrapper">

      {/* Full-page SVG background — cloud pattern behind entire page */}
      <div
        className="product-page-svg-bg"
        aria-hidden="true"
        dangerouslySetInnerHTML={{ __html: '' }}
        ref={el => {
          if (el && product.cloudSvg) {
            fetch(`/assets/${product.cloudSvg}`)
              .then(r => r.text())
              .then(svg => { el.innerHTML = svg; })
              .catch(() => {});
          }
        }}
      />

      <div className="product-page">
        <div className="product-page-layout">

          {/* LEFT: Media — main image + thumbnail grid below */}
          <div className="product-media">

            {/* Main image */}
            <div className="product-media-main">
              {hasImages && !activeImgFailed ? (
                <img
                  key={activeIdx}
                  className="product-media-photo"
                  src={`/assets/${images[activeIdx]}`}
                  alt={product.title}
                  onError={() => setFailedImgs(f => ({ ...f, [activeIdx]: true }))}
                />
              ) : (
                <div className="product-media-placeholder">
                  <span>image not in archive</span>
                </div>
              )}
            </div>

            {/* Thumbnail grid below main image (matches screenshot layout) */}
            {images.length > 1 && (
              <div className="product-thumbnails">
                {images.map((img, i) => (
                  <div
                    key={i}
                    className={`product-thumbnail${i === activeIdx ? ' active' : ''}`}
                    onClick={() => setActiveIdx(i)}
                  >
                    <img
                      src={`/assets/${img}`}
                      alt={`${product.title} view ${i + 1}`}
                      onError={() => setFailedImgs(f => ({ ...f, [i]: true }))}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* RIGHT: Info */}
          <div className="product-info">
            <h1 className="product-title">{product.title}</h1>
            <div className="product-price">{fmt(product.price)}</div>

            {/* Size picker */}
            {product.sizes && product.sizes.length > 0 && (
              <div className="variant-group">
                <div className="variant-pills">
                  {product.sizes.map(s => (
                    <button
                      key={s}
                      className={`variant-pill ${selectedSize === s ? 'active' : ''}`}
                      onClick={() => setSelectedSize(s)}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="archive-note">
              This site is an archive, but the sweaters are still available! <br/>Pls email rachelbinx @ gmail :)
            </div>

            <div className="product-description">
              <p>{product.description}</p>
            </div>

            {product.materials && (
              <div className="product-materials">
                {product.materials}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Related products */}
      {related.length > 0 && (
        <div className="related-section">
          <h3 style={{ marginBottom: "2rem"}}>you might also like</h3>
          <ul className="grid grid--3-col-desktop grid--2-col-tablet-down" role="list">
            {related.map(p => (
              <ProductCard key={p.id} product={p} onClick={onProductClick} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ─── ROUTING ─────────────────────────────────────────────────
function parsePath() {
  const path = window.location.pathname;
  const productMatch = path.match(/^\/products\/(.+)$/);
  if (productMatch) return { view: 'product', slug: productMatch[1] };
  return { view: 'home' };
}

export default function App() {
  const [route, setRoute] = useState(parsePath);

  useEffect(() => {
    const onPopState = () => setRoute(parsePath());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  function navigate(path) {
    window.history.pushState({}, '', path);
    window.scrollTo(0, 0);
    setRoute(parsePath());
  }

  function handleProductClick(product) {
    navigate(`/products/${product.slug}`);
  }

  function handleHome() {
    navigate('/');
  }

  function handleBack() {
    navigate('/');
  }

  const activeProduct = route.view === 'product'
    ? productsData.products.find(p => p.slug === route.slug)
    : null;

  return (
    <>
      <Header onHome={handleHome} />

      <main id="MainContent" style={{ minHeight: '70vh' }}>
        {route.view === 'home' && (
          <HomePage onProductClick={handleProductClick} />
        )}
        {route.view === 'product' && activeProduct && (
          <ProductPage
            product={activeProduct}
            onBack={handleBack}
            onProductClick={handleProductClick}
          />
        )}
        {route.view === 'product' && !activeProduct && (
          <div className="page-width" style={{ padding: '4rem 5rem' }}>
            <p>Product not found. <button className="back-btn" onClick={handleHome}>← Back to home</button></p>
          </div>
        )}
      </main>

      <Footer />
    </>
  );
}
